/**
 * What somebody typed into the search, turned into fields — §5.3, T1.
 *
 * The search's last row is **New contact** with what was typed already in
 * the right boxes: *renee castillo 7175550193* is a first name, a last name
 * and a mobile, and nobody should type any of it twice. When the text reads
 * as a street instead — it starts with a house number, or ends in a street
 * word — the row offers **A new property on <street>**, and the text goes to
 * the address lookup.
 */
import { formatPhone, phoneDigits } from '../phone.js';
/** A run of digits and the punctuation people write numbers with. */
const NUMBER_RUN = /\+?\(?\d[\d\s().-]*\d\)?/g;
/**
 * The words a street name ends in — USPS suffixes, written out and short.
 * Only consulted when the text has no phone or email in it.
 */
const STREET_WORDS = new Set([
    'st', 'street', 'rd', 'road', 'dr', 'drive', 'ave', 'av', 'avenue', 'ln', 'lane',
    'ct', 'court', 'way', 'blvd', 'boulevard', 'pl', 'place', 'cir', 'circle', 'ter',
    'terrace', 'pkwy', 'parkway', 'hwy', 'highway', 'trl', 'trail', 'rdg', 'ridge',
    'run', 'pike', 'sq', 'square', 'loop', 'xing', 'crossing', 'holw', 'hollow',
]);
/** A house number: 418, 418A, 418-B. */
const HOUSE_NUMBER = /^\d{1,6}(-?[a-z])?$/i;
/**
 * A word typed in lower case gets its capitals; one typed with any capital
 * is left exactly as typed, so *McDonald* and *DeLuca* survive.
 */
export function capitalise(word) {
    if (word !== word.toLowerCase())
        return word;
    return word.replace(/(^|[-'’])(\p{L})/gu, (_, before, letter) => before + letter.toUpperCase());
}
export function parseTyped(text) {
    const result = {
        firstName: null, lastName: null, phone: null, email: null, street: null,
    };
    let rest = ` ${text} `;
    /* A run of seven digits or more is a phone number, whatever wraps it. */
    for (const match of text.matchAll(NUMBER_RUN)) {
        if (phoneDigits(match[0]).length >= 7) {
            result.phone = formatPhone(match[0]);
            rest = rest.replace(match[0], ' ');
            break;
        }
    }
    let words = rest.split(/\s+/).map((word) => word.replace(/^[,;]+|[,;]+$/g, '')).filter(Boolean);
    const at = words.findIndex((word) => word.includes('@'));
    if (at >= 0) {
        result.email = words[at].toLowerCase();
        words = words.filter((_, index) => index !== at);
    }
    if (words.length === 0)
        return result;
    /* A street only when nothing else in the text was a person's detail. */
    const last = words[words.length - 1].toLowerCase().replace(/\.$/, '');
    const looksLikeStreet = !result.phone && !result.email && ((HOUSE_NUMBER.test(words[0]) && words.length > 1) || STREET_WORDS.has(last));
    if (looksLikeStreet) {
        const name = (HOUSE_NUMBER.test(words[0]) ? words.slice(1) : words).map(capitalise).join(' ');
        result.street = { text: text.trim(), name };
        return result;
    }
    const named = words.map(capitalise);
    if (named.length === 1) {
        result.lastName = named[0];
    }
    else {
        result.firstName = named[0];
        result.lastName = named.slice(1).join(' ');
    }
    return result;
}
//# sourceMappingURL=parse.js.map